import React, { useRef, useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Divider, Result, Space } from 'antd';
import axios from 'axios';
import ApiService from '../APIServices/ApiService';
import Turnstile from '../components/Turnstile';

const { Title, Paragraph, Text } = Typography;

/**
 * The PUBLIC account-deletion page.
 *
 * WHY IT LOOKS LIKE THIS. Google Play requires a publicly reachable URL —
 * reachable without signing in, and without installing anything — that names
 * the app, explains how to ask for deletion, and says WHAT IS DELETED and
 * WHAT IS KEPT, with how long. It was a bare three-field form: no app name, no
 * explanation, no mention of what happens to anything. A reviewer landing on
 * it could not tell which app it belonged to, and the policy check fails on
 * exactly that.
 *
 * The form itself is unchanged — same fields, same Turnstile, same endpoint.
 * What is added around it is the part the policy is actually about.
 *
 * IT MUST STAY OUTSIDE THE AUTH GUARD. The whole point is that somebody who
 * can no longer get into their account can still ask for it to be removed; a
 * page behind a login is no use to the person most likely to need it.
 */
const APP_NAME = 'YouGuide One';
const COMPANY = 'YouGuide';

/** What an approved request removes, in the customer's own words. */
const DELETED = [
    'Your account and sign-in details',
    'Your profile, including your name and email address',
    'Your trips, trip points, photos and videos',
    'Your saved guides and phrasebooks, and your library',
    'Your messages in trip chats',
    'Any device tokens used to send you notifications',
];

/**
 * What survives, and WHY.
 *
 * Named rather than glossed over. A page that promises everything is erased
 * and then keeps an invoice is worse than one that says so plainly, and the
 * policy asks for this list specifically.
 */
const RETAINED = [
    {
        what: 'Purchase and payment records',
        why: 'We are required to keep records of sales for tax and accounting purposes. '
            + 'They are kept for as long as the law requires and are not used for anything else.',
    },
    {
        what: 'An eSIM already installed on your phone',
        why: 'An eSIM is issued by a mobile network, not by us. Deleting your account removes '
            + 'it from the app; it does not cancel the data plan on your device. Remove the '
            + 'eSIM in your phone’s settings if you no longer want it.',
    },
];

const DeleteAccount = () => {
    const [loading, setLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const turnstileRef = useRef(null);
    const [form] = Form.useForm();

    const onFinish = async (values) => {
        if (!captchaToken) {
            setError('Please complete the check above to confirm you are not a robot.');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await axios.post(
                ApiService.URLL + '/delete-account',
                { ...values, captchaToken },
            );
            // Shown as a PAGE, not a toast that disappears. This is the only
            // confirmation the person gets that their request was recorded,
            // and a message that vanishes after three seconds is not one.
            setSubmitted(response.data?.message
                || 'Your request has been received.');
            form.resetFields();
        } catch (err) {
            setError(err.response?.data?.message
                || 'We could not submit your request. Please try again, or email '
                   + 'support@youguide.com and we will handle it by hand.');
        } finally {
            // Turnstile tokens are single-use — re-arm so a retry can succeed.
            setCaptchaToken('');
            if (turnstileRef.current) turnstileRef.current.reset();
            setLoading(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '32px 16px' }}>
            <Card style={{ maxWidth: 720, margin: '0 auto' }}>
                <Title level={3} style={{ marginBottom: 4 }}>
                    Delete your {APP_NAME} account
                </Title>
                <Text type="secondary">
                    {COMPANY} &middot; youguide.com
                </Text>

                <Paragraph style={{ marginTop: 20 }}>
                    This page is for customers of <strong>{APP_NAME}</strong> and the YouGuide
                    website who want their account and personal data removed. You do not need to
                    be signed in to use it.
                </Paragraph>

                {submitted ? (
                    <Result
                        status="success"
                        title="Request received"
                        subTitle={submitted}
                        extra={(
                            <Paragraph type="secondary" style={{ maxWidth: 460, margin: '0 auto' }}>
                                We will email you at the address you gave when your account has
                                been removed. If you did not mean to send this, email{' '}
                                <a href="mailto:support@youguide.com">support@youguide.com</a>{' '}
                                and we will cancel it.
                            </Paragraph>
                        )}
                    />
                ) : (
                    <>
                        <Title level={5}>How to request deletion</Title>
                        <Paragraph>
                            There are two ways, and both reach the same place:
                        </Paragraph>
                        <ol style={{ paddingLeft: 20, marginBottom: 20 }}>
                            <li style={{ marginBottom: 6 }}>
                                <strong>In the app</strong> — open {APP_NAME}, go to
                                {' '}<strong>Profile</strong> and choose
                                {' '}<strong>Delete my account</strong>.
                            </li>
                            <li>
                                <strong>Here</strong> — fill in the form below. Use the email
                                address your account is registered with, or we will not be able
                                to find it.
                            </li>
                        </ol>

                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 20 }}
                            message="Deletion is permanent"
                            description={(
                                <>
                                    Once your account is deleted it cannot be restored, and
                                    anything you bought through it can no longer be opened from
                                    the app. Guides you have already downloaded to your phone
                                    stay on your phone.
                                </>
                            )}
                        />

                        <Title level={5}>What is deleted</Title>
                        <ul style={{ paddingLeft: 20 }}>
                            {DELETED.map((item) => <li key={item}>{item}</li>)}
                        </ul>

                        <Title level={5} style={{ marginTop: 20 }}>What is kept, and why</Title>
                        <ul style={{ paddingLeft: 20 }}>
                            {RETAINED.map((item) => (
                                <li key={item.what} style={{ marginBottom: 8 }}>
                                    <strong>{item.what}.</strong> {item.why}
                                </li>
                            ))}
                        </ul>

                        <Title level={5} style={{ marginTop: 20 }}>How long it takes</Title>
                        <Paragraph>
                            We review every request by hand to make sure the account belongs to
                            the person asking. Deletion is completed within{' '}
                            <strong>30 days</strong> of your request, and usually much sooner.
                            We will email you when it is done.
                        </Paragraph>

                        <Divider />

                        <Title level={5}>Request deletion</Title>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            requiredMark={false}
                        >
                            <Form.Item
                                label="Full name"
                                name="name"
                                rules={[{ required: true, message: 'Please enter your full name.' }]}
                            >
                                <Input placeholder="The name on your account" />
                            </Form.Item>

                            <Form.Item
                                label="Email address"
                                name="email"
                                extra="This must be the address your account is registered with."
                                rules={[
                                    { required: true, message: 'Please enter your email address.' },
                                    { type: 'email', message: 'That does not look like an email address.' },
                                ]}
                            >
                                <Input placeholder="you@example.com" />
                            </Form.Item>

                            <Form.Item
                                label="Why are you leaving?"
                                name="reason"
                                rules={[{ required: true, message: 'Please tell us briefly why.' }]}
                            >
                                <Input.TextArea rows={4} placeholder="A sentence is plenty." />
                            </Form.Item>

                            <Form.Item>
                                <Turnstile
                                    ref={turnstileRef}
                                    theme="light"
                                    onVerify={setCaptchaToken}
                                    onExpire={() => setCaptchaToken('')}
                                    onError={() => setCaptchaToken('')}
                                />
                            </Form.Item>

                            {error && (
                                <Alert
                                    type="error"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                    message={error}
                                />
                            )}

                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button
                                    type="primary"
                                    danger
                                    htmlType="submit"
                                    loading={loading}
                                    disabled={!captchaToken}
                                    block
                                >
                                    Request account deletion
                                </Button>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Prefer to write to us? Email{' '}
                                    <a href="mailto:support@youguide.com">support@youguide.com</a>{' '}
                                    from your registered address and we will do the same thing.
                                </Text>
                            </Space>
                        </Form>
                    </>
                )}
            </Card>
        </div>
    );
};

export default DeleteAccount;
